// @ts-check

class AtaPage extends HTMLElement {
  constructor() {
    super();

    this.config = {
      useState: this.hasAttribute("use-state"),
    };

    /** @type {{[key: string]: string}} */
    this.ATA_DISPLAY_VALUES = {
      0: "OVERLAP",
      overlap: "OVERLAP",
      1: "TOUCH",
      touch: "TOUCH",
      2: "NOT TOUCH",
      "dont-touch": "NOT TOUCH",
    };

    this.refs = {
      /** @type {() => NodeListOf<HTMLInputElement>} */
      ataSelectors: () => this.querySelectorAll("input[data-ata-input]"),
      /** @type {() => HTMLDivElement | null} */
      ataImage: () => this.querySelector("#ata-image"),
    };

    /** @type {Record<'ata', string | undefined>} */
    this.giftInitialValues = {
      ata: undefined,
    };

    /** @type {Partial<Record<'ata', string | undefined>>} */
    this.defaultValues = {
      ata: this.getAttribute("initial-ata") ?? this.giftInitialValues.ata,
    };

    /** @type {Map<'ata', string>} */
    this.currentValues = new Map();

    /** @type {PromiseWithResolvers<boolean>} */
    this.readyPromise = Promise.withResolvers();
  }

  connectedCallback() {
    if (this.config.useState) {
      this.setDefaultValues();
    }

    this.setAtaValue(this.defaultValues.ata);
    this.assignAtaListeners();
    this.readyPromise.resolve(true);
  }

  disconnectedCallback() {
    this.readyPromise.reject();
  }

  setDefaultValues() {
    const savedValues = getInitialValues(["ata"]);
    if (savedValues) {
      this.defaultValues = savedValues;
    }
  }

  assignAtaListeners() {
    const selectors = this.refs.ataSelectors();
    selectors.forEach((s) => {
      s.addEventListener("change", () => this.onAtaChange(s));
    });
  }

  /**
   * @param {'ata'} attr
   * @param {string} value
   */
  updateState(attr, value) {
    if (this.config.useState) {
      this.currentValues.set(attr, value);
      updateURLSearchParams({ [attr]: value });
    }
  }

  /** @param {'ata'} attr */
  resetState(attr) {
    if (this.config.useState) {
      this.currentValues.delete(attr);
      updateURLSearchParams({ [attr]: undefined });
    }
  }

  /** @param {string | undefined} value */
  setAtaValue(value) {
    let isSet = false;
    const selectors = this.refs.ataSelectors();
    selectors.forEach((selector) => {
      if (value === selector.value) {
        this.onAtaChange(selector);
        isSet = true;
      }
    });
    if (!isSet) {
      selectors.forEach((selector) => {
        selector.checked = false;
      });
      this.resetState("ata");
      this.setAtaImage();
    }
  }

  /** @param {Record<'ata', string | undefined>} obj */
  async setValues({ ata }) {
    await this.readyPromise.promise;
    this.setAtaValue(ata);
  }

  async getValues() {
    await this.readyPromise.promise;
    return this.currentValues;
  }

  async resetValues() {
    await this.readyPromise.promise;
    await this.setValues(this.giftInitialValues);
  }

  /** @param {HTMLInputElement} selector */
  onAtaChange(selector) {
    switch (selector.value) {
      case "1":
      case "touch": {
        selector.checked = true;
        this.updateState("ata", selector.value);
        this.setAtaImage("ata_touch.svg");
        break;
      }
      case "2":
      case "dont-touch": {
        selector.checked = true;
        this.updateState("ata", selector.value);
        this.setAtaImage("ata_not_touch.svg");
        break;
      }
      case "0":
      case "overlap": {
        selector.checked = true;
        this.updateState("ata", selector.value);
        this.setAtaImage("ata_overlap.svg");
        break;
      }
      default: {
        selector.checked = false;
        this.resetState("ata");
        this.setAtaImage();
      }
    }
  }

  /** @param {'ata_touch.svg' | 'ata_not_touch.svg' | 'ata_overlap.svg' | 'ata_default.svg'} [filename] */
  setAtaImage(filename = "ata_default.svg") {
    const imageContainer = this.refs.ataImage();
    if (!imageContainer) {
      throw new Error("Ata image container not found");
    }
    imageContainer.style.backgroundImage = `url(${fileUrl}${filename})`;
  }

  /** @param {string | undefined} value */
  validateAtaValue(value) {
    if (!value) {
      return false;
    }
    return this.ATA_DISPLAY_VALUES.hasOwnProperty(value);
  }

  /** @param {string} value */
  displayAta(value) {
    return this.ATA_DISPLAY_VALUES[value];
  }
}

if (!customElements.get("ata-page")) {
  customElements.define("ata-page", AtaPage);
}
